import React, { useMemo } from "react";
import { Field, getFormValues } from "redux-form";
import { useSelector } from "react-redux";

import { ASSET_TYPES } from "utils/constants";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { getCardIndexFromName, isRequiredAssetField } from "social/utils";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import DynamicInput from "social/DynamicInput";
import { SOCIAL_WIZARD } from "widgets/forms/list";

import { LINKEDIN_CAROUSEL_CONSTRAINTS } from "social/validators/linkedIn";
import { imposiumCompositionForCard } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";

import "./CarouselCardForm.css";



const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_MB,
	IMAGE_ACCEPT,
} = LINKEDIN_CAROUSEL_CONSTRAINTS;

const LINKEDIN_IMAGE_REQUIREMENTS = [
	{
		name: "File Size",
		description: `${MAX_IMAGE_MB}MB Maximum`,
	},
	{
		name: "File Types",
		description: IMAGE_ACCEPT,
	},
	{
		name: "Dimensions",
		description: "Recommended: 1200 x 1200 pixels, greater than 360 x 360 pixels",
	}
];

export const LINKEDIN_CAROUSEL_FIELDS = {
	imposiumProject,
	imposiumComposition: imposiumCompositionForCard,
	image: {
		name: "image",
		label: "Image Asset",
		className: "full-width cypress-image-select",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_product`,
		requirements: LINKEDIN_IMAGE_REQUIREMENTS,
		required: true,
		accept: IMAGE_ACCEPT,
	},
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width cypress-headline",
		placeholder: "Enter a short headline",
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_headline`,
		tip: {
			description: "May get truncated after 70 characters",
			direction: "right",
		},
		charMax: MAX_LENGTH_HEADLINE,
		// showCharCount: true,
		required: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width cypress-landing-page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_clickTag1`,
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
		required: true,
	},
};


function fieldGenerator(props) {
	const {
		libraryId,
		cardPrefix,
		creativeAssetType,
		navigationActions,
		isViewOnly = false,
		showPristineErrors = false,
		doVersionsExist,
	} = props;

	return (name) => {
		const fieldProps = LINKEDIN_CAROUSEL_FIELDS[name];

		let extraProps = {
			disabled: isViewOnly,
			showPristineErrors,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if(/image|video/i.test(fieldProps.name)) {
			extraProps.libraryId = libraryId;
			extraProps.navigationActions = navigationActions;
			extraProps.creativeAssetType = creativeAssetType;
			extraProps.required = isRequiredAssetField(creativeAssetType);
		}

		if(cardPrefix === "cards[0]" && name === "imposiumComposition"){
			extraProps.showProjectName = false;
		}

		return (
			<Field
				key={fieldProps.label}
				{...fieldProps}
				{...extraProps}
				name = {`${cardPrefix}.${fieldProps.name}`}
			/>
		);
	};
}

export default function LinkedInCarouselCardForm(props) {
	const {
		libraryId,
		navigationActions,
		adType,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	} = props;

	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {});

	const currentCard = values?.currentCard || 0;
	const cardPrefix = `cards[${currentCard}]`;

	const creativeAssetType = values?.assetType;

	const fields = useMemo(() => {
		const generateFields = fieldGenerator({
			libraryId,
			cardPrefix,
			creativeAssetType,
			isViewOnly,
			showPristineErrors,
			navigationActions,
			doVersionsExist,
		});

		return Object.keys(LINKEDIN_CAROUSEL_FIELDS).map(generateFields);
	}, [
		libraryId,
		cardPrefix,
		creativeAssetType,
		isViewOnly,
		showPristineErrors,
		navigationActions,
		doVersionsExist
	]);

	return (
		<div className="carousel-card-form">
			<div className="fields">
				{fields}
			</div>
		</div>
	);
}
